import {
  and,
  asc,
  desc,
  eq,
  gte,
  habit,
  habit_completion,
  habit_reminder,
  habit_version,
  isNotNull,
  isNull,
  lte,
  type PowerSyncDrizzleDB,
  streak_protector,
} from "@habits/powersync";
import { liveHabitFilters } from "../live/filters";

/**
 * Query builders for the habits domain. They all operate on PowerSync's local
 * SQLite—zero network.
 */

export function getHabitsQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  opts: { includeArchived?: boolean } = {},
) {
  // Trashed habits (deleted_at) do NOT exist for any normal view:
  // Today, Habits, calendar, stats, and streaks stay clean. The
  // "live = deleted_at IS NULL" invariant is centralized in liveHabitFilters().
  const alive = and(eq(habit.user_id, userId), ...liveHabitFilters());
  const where = opts.includeArchived ? alive : and(alive, eq(habit.archived, 0));
  return db
    .select()
    .from(habit)
    .where(where)
    .orderBy(asc(habit.archived), asc(habit.sort_order), asc(habit.created_at));
}

export type HabitRow = Awaited<ReturnType<ReturnType<typeof getHabitsQuery>["execute"]>>[number];

export function getHabitByIdQuery(db: PowerSyncDrizzleDB, habitId: string) {
  return db.select().from(habit).where(eq(habit.id, habitId)).limit(1);
}

/** Trash (L2): the user's deleted habits, most recent first. */
export function getTrashedHabitsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(habit)
    .where(and(eq(habit.user_id, userId), isNotNull(habit.deleted_at)))
    .orderBy(desc(habit.deleted_at));
}

/** Completions for ALL of the user's habits within a day-key range. */
export function getCompletionsRangeQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  fromKey: string,
  toKey: string,
) {
  return db
    .select()
    .from(habit_completion)
    .where(
      and(
        eq(habit_completion.user_id, userId),
        gte(habit_completion.date, fromKey),
        lte(habit_completion.date, toKey),
      ),
    )
    .orderBy(asc(habit_completion.date));
}

export type CompletionRow = Awaited<
  ReturnType<ReturnType<typeof getCompletionsRangeQuery>["execute"]>
>[number];

/** Full history for one habit (for streaks and the calendar). */
export function getHabitCompletionsQuery(db: PowerSyncDrizzleDB, habitId: string) {
  return db
    .select()
    .from(habit_completion)
    .where(eq(habit_completion.habit_id, habitId))
    .orderBy(desc(habit_completion.date));
}

/** Full user history (streaks on "Today" and statistics). */
export function getAllCompletionsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(habit_completion)
    .where(eq(habit_completion.user_id, userId))
    .orderBy(asc(habit_completion.date));
}

/**
 * Synced DEFINITION versions (offline window, ≤10/habit) for ALL of the user's
 * habits. These rows build, per habit, the timeline that resolves the definition
 * in effect by date (version-timeline.ts). Sorted ascending by effective date.
 */
export function getUserHabitVersionsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(habit_version)
    .where(eq(habit_version.user_id, userId))
    .orderBy(asc(habit_version.server_ts), asc(habit_version.version_number));
}

// The canonical `HabitVersionRow` type lives in `version-timeline.ts`
// (a pure structural interface with no drizzle dependency) and is reexported by
// the barrel; it is NOT redefined here to avoid `export *` ambiguity.

/** Versions (offline window) for ONE habit—history in the detail view. */
export function getHabitVersionsQuery(db: PowerSyncDrizzleDB, habitId: string) {
  return db
    .select()
    .from(habit_version)
    .where(eq(habit_version.habit_id, habitId))
    .orderBy(desc(habit_version.version_number));
}

/**
 * The user's LIVE streak protectors (inventory + already spent ones). The streak
 * engine needs them all: spent ones provide protected days and free ones provide
 * available inventory.
 */
export function getStreakProtectorsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(streak_protector)
    .where(and(eq(streak_protector.user_id, userId), isNull(streak_protector.deleted_at)))
    .orderBy(asc(streak_protector.granted_at));
}

export type StreakProtectorDbRow = Awaited<
  ReturnType<ReturnType<typeof getStreakProtectorsQuery>["execute"]>
>[number];

export function getRemindersQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(habit_reminder)
    .where(eq(habit_reminder.user_id, userId))
    .orderBy(asc(habit_reminder.time_of_day));
}

export type ReminderRow = Awaited<
  ReturnType<ReturnType<typeof getRemindersQuery>["execute"]>
>[number];
